import React, { useEffect, useState } from 'react';
import { Tv, Copy, ExternalLink, RefreshCw, KeyRound } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from './ui/alert-dialog';
import { painelTvService } from '../services/painelTvService';
import { useToast } from '../hooks/use-toast';
import { PainelTvToken } from '../types/operacoes';

const extractError = (e: any) => e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Erro inesperado';

const PainelTvConfig: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [confirmarAberto, setConfirmarAberto] = useState(false);
  const [dados, setDados] = useState<PainelTvToken>({ token: null, geradoEm: null });

  useEffect(() => {
    painelTvService
      .getToken()
      .then(setDados)
      .catch((e) => toast({ title: 'Erro', description: extractError(e), variant: 'destructive' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const url = dados.token ? `${window.location.origin}/painel-tv/${dados.token}` : '';

  const gerar = async () => {
    setConfirmarAberto(false);
    setGerando(true);
    try {
      const novo = await painelTvService.gerarToken();
      setDados(novo);
      toast({
        title: 'Novo token gerado',
        description: dados.token ? 'O link anterior foi revogado. Atualize o link nas TVs.' : 'Abra o link na TV de monitoramento.'
      });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setGerando(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copiado' });
    } catch {
      toast({ title: 'Não foi possível copiar', description: 'Selecione o link e copie manualmente.', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Carregando...</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Tv className="h-6 w-6 dark:text-white" />
        <h1 className="text-2xl font-bold dark:text-white">Painel TV</h1>
      </div>
      <p className="text-gray-500 dark:text-gray-400 mb-6">
        Painel de monitoramento para TV, atualizado em tempo real. O acesso é feito por um link com token, sem login.
        Qualquer pessoa com o link vê o painel, então não o compartilhe fora da equipe. Ao gerar um novo token, o link anterior
        deixa de funcionar na hora e as TVs abertas com ele são desconectadas.
      </p>

      <Card className="p-5 space-y-4">
        {dados.token ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="painel-tv-url">Link do painel</Label>
              <div className="flex gap-2">
                <Input id="painel-tv-url" readOnly value={url} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
                <Button variant="outline" size="icon" title="Copiar link" onClick={copiar}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" title="Abrir em nova aba" onClick={() => window.open(url, '_blank', 'noopener')}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              {dados.geradoEm && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Token gerado em {new Date(dados.geradoEm).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <Button variant="destructive" onClick={() => setConfirmarAberto(true)} disabled={gerando} className="gap-2">
                <RefreshCw className={`h-4 w-4 ${gerando ? 'animate-spin' : ''}`} />
                Gerar novo token
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center text-center gap-3 py-4">
            <KeyRound className="h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum token gerado ainda. Gere um para obter o link do painel.</p>
            <Button onClick={gerar} disabled={gerando} className="gap-2">
              <KeyRound className="h-4 w-4" />
              {gerando ? 'Gerando...' : 'Gerar token'}
            </Button>
          </div>
        )}
      </Card>

      <AlertDialog open={confirmarAberto} onOpenChange={setConfirmarAberto}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerar novo token?</AlertDialogTitle>
            <AlertDialogDescription>
              O link atual será revogado e todas as TVs abertas com ele deixarão de exibir o painel. Será preciso abrir o novo link em cada TV.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={gerar}>Gerar novo token</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PainelTvConfig;
